import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ScrollArea,
  toast,
} from "@devhop/ui";
import { Loader2, Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { BranchProductWithDetails } from "../../domain/types";
import { useAddOrderItems } from "../api/queries";
import type { CartItem } from "../context/CartContext";
import { usePOSContext } from "../context/POSContext";
import {
  clearCustomerDisplayTempCart,
  setCustomerDisplayTempCart,
} from "../lib/customer-display-temp-cart";
import { printSupplementaryBillAndKitchen } from "../lib/print-order-bill";
import { ProductConfigDialog } from "./ProductConfigDialog";
import { ProductMenu } from "./ProductMenu";

type AddItemsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  onSuccess?: () => void;
  onLocalSubmit?: (items: CartItem[]) => Promise<void>;
};

// Local cart state type (same as CartItem but without localStorage)
type LocalCartItem = CartItem;

export function AddItemsDialog({
  open,
  onOpenChange,
  orderId,
  onSuccess,
  onLocalSubmit,
}: AddItemsDialogProps) {
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;

  const [localCartItems, setLocalCartItems] = useState<LocalCartItem[]>([]);
  const [selectedProduct, setSelectedProduct] =
    useState<BranchProductWithDetails | null>(null);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);
  const addOrderItemsMutation = useAddOrderItems();
  const isSubmitting = addOrderItemsMutation.isPending || isSubmittingLocal;
  /**
   * Radix nested Dialog: ปิด ProductConfigDialog แล้ว parent มักได้ onOpenChange(false)
   * → ถ้าไม่กัน จะเรียก handleClose() ล้าง localCartItems / ปิด AddItemsDialog ทั้งก้อน
   */
  const suppressParentCloseRef = useRef(false);

  const closeProductConfig = () => {
    suppressParentCloseRef.current = true;
    setIsConfigDialogOpen(false);
    setSelectedProduct(null);
    window.setTimeout(() => {
      suppressParentCloseRef.current = false;
    }, 400);
  };

  // Calculate total price helper
  const calculateTotalPrice = (
    price: number,
    modifiers: CartItem["modifiers"],
    quantity: number,
  ): number => {
    const modifierTotal = modifiers.reduce(
      (sum, mod) => sum + mod.priceDelta,
      0,
    );
    return (price + modifierTotal) * quantity;
  };

  // Add item to local cart
  const handleAddItem = (item: Omit<CartItem, "id" | "totalPrice">) => {
    const id = `${item.variantId}-${Date.now()}-${Math.random()}`;
    const totalPrice = calculateTotalPrice(
      item.price,
      item.modifiers,
      item.quantity,
    );

    const newItem: LocalCartItem = {
      ...item,
      id,
      totalPrice,
    };

    setLocalCartItems((prev) => [...prev, newItem]);
    // ปิด config dialog ผ่าน closeProductConfig — อย่าลืมว่า ProductConfigDialog จะเรียก onClose() ต่อท้าย
  };

  // Remove item from local cart
  const handleRemoveItem = (itemId: string) => {
    setLocalCartItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  // Update quantity
  const handleUpdateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveItem(itemId);
      return;
    }

    setLocalCartItems((prev) =>
      prev.map((item) => {
        if (item.id === itemId) {
          const totalPrice = calculateTotalPrice(
            item.price,
            item.modifiers,
            quantity,
          );
          return { ...item, quantity, totalPrice };
        }
        return item;
      }),
    );
  };

  // Handle product click
  const handleProductClick = (product: BranchProductWithDetails) => {
    setSelectedProduct(product);
    setIsConfigDialogOpen(true);
  };

  // Handle close config dialog (ຍົກເລີກ / ຫຼັງເພີ່ມສິນຄ້າ — ProductConfigDialog เรียก onClose หลัง addItem)
  const handleCloseConfigDialog = () => {
    closeProductConfig();
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!branchId) {
      toast.error("ກະລຸນາເລືອກສາຂາ");
      return;
    }

    if (localCartItems.length === 0) {
      toast.error("ກະລຸນາເລືອກສິນຄ້າກ່ອນ");
      return;
    }

    try {
      if (onLocalSubmit) {
        setIsSubmittingLocal(true);
        await onLocalSubmit(localCartItems);
        toast.success("ເພີ່ມສິນຄ້າສຳເລັດແລ້ວ");
        setLocalCartItems([]);
        onSuccess?.();
        onOpenChange(false);
        return;
      }

      const result = await addOrderItemsMutation.mutateAsync({
        orderId,
        branchId,
        cartItems: localCartItems,
      });

      // พิมพ์ Supplementary Bill (รายการที่เพิ่ม) + Kitchen tickets ของรายการเพิ่ม
      const newOrderItemIds = result.items
        .slice(-localCartItems.length)
        .map((item) => item.id);
      if (newOrderItemIds.length > 0 && branchId) {
        try {
          await printSupplementaryBillAndKitchen(
            orderId,
            branchId,
            newOrderItemIds,
          );
        } catch (printErr) {
          toast.error(
            printErr instanceof Error ? printErr.message : "ເກີດຂໍ້ຜິດພາດໃນການພິມ",
          );
        }
      }

      toast.success("ເພີ່ມສິນຄ້າສຳເລັດແລ້ວ");
      setLocalCartItems([]);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການເພີ່ມສິນຄ້າ",
      );
    } finally {
      setIsSubmittingLocal(false);
    }
  };

  // Sync cart ไป Customer Display — เมื่อเปิด dialog และ localCartItems เปลี่ยน
  useEffect(() => {
    if (!open) {
      clearCustomerDisplayTempCart();
      return;
    }
    if (localCartItems.length > 0) {
      setCustomerDisplayTempCart(localCartItems);
    } else {
      clearCustomerDisplayTempCart();
    }
  }, [open, localCartItems]);

  // Handle dialog close
  const handleClose = () => {
    if (isSubmitting) return;
    clearCustomerDisplayTempCart();
    setLocalCartItems([]);
    setSelectedProduct(null);
    setIsConfigDialogOpen(false);
    onOpenChange(false);
  };

  const total = localCartItems.reduce((sum, item) => sum + item.totalPrice, 0);

  const handleMainDialogOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return;
    if (suppressParentCloseRef.current) return;
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleMainDialogOpenChange}>
        <DialogContent
          className="!max-w-6xl flex h-[90vh] flex-col"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>ເພີ່ມສິນຄ້າເຂົ້າ Order</DialogTitle>
          </DialogHeader>

          <div className="flex flex-1 gap-4 overflow-hidden">
            {/* Product Menu Section */}
            <div className="flex flex-1 flex-col overflow-hidden">
              <ScrollArea className="flex-1 overflow-auto">
                <ProductMenu onProductClick={handleProductClick} />
              </ScrollArea>
            </div>

            {/* Cart Summary Section */}
            <div className="flex w-80 flex-col border-l">
              <div className="border-b p-4">
                <h3 className="font-semibold text-sm">ລາຍການທີ່ຈະເພີ່ມ</h3>
              </div>

              <ScrollArea className="flex-1">
                <div className="space-y-3 p-4">
                  {localCartItems.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground text-sm">
                      ຍັງບໍ່ມີສິນຄ້າ
                    </div>
                  ) : (
                    localCartItems.map((item) => (
                      <div
                        key={item.id}
                        className="space-y-2 rounded-lg border p-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <h4 className="truncate font-medium text-sm">
                              {item.productName}
                            </h4>
                            {item.variantName && (
                              <p className="truncate text-muted-foreground text-xs">
                                {item.variantName}
                              </p>
                            )}
                            {item.modifiers.length > 0 && (
                              <div className="mt-1 space-y-0.5">
                                {item.modifiers.map((modifier) => (
                                  <p
                                    key={modifier.modifierId}
                                    className="text-muted-foreground text-xs"
                                  >
                                    + {modifier.modifierName}:{" "}
                                    {modifier.optionLabel}
                                    {modifier.priceDelta !== 0 && (
                                      <span className="ml-1">
                                        ({modifier.priceDelta > 0 ? "+" : ""}
                                        {modifier.priceDelta.toLocaleString(
                                          "lo-LA",
                                        )}{" "}
                                        ₭)
                                      </span>
                                    )}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleRemoveItem(item.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() =>
                                handleUpdateQuantity(item.id, item.quantity - 1)
                              }
                            >
                              -
                            </Button>
                            <span className="w-8 text-center font-medium text-sm">
                              {item.quantity}
                            </span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() =>
                                handleUpdateQuantity(item.id, item.quantity + 1)
                              }
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-sm">
                              {item.totalPrice.toLocaleString("lo-LA")} ₭
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>

              {localCartItems.length > 0 && (
                <div className="space-y-2 border-t p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">ລາຄາລວມ:</span>
                    <span className="font-bold text-lg">
                      {total.toLocaleString("lo-LA")} ₭
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              ຍົກເລີກ
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || localCartItems.length === 0}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ກຳລັງເພີ່ມ...
                </>
              ) : (
                "ເພີ່ມສິນຄ້າ"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Config Dialog */}
      <ProductConfigDialog
        product={selectedProduct}
        open={isConfigDialogOpen}
        onClose={handleCloseConfigDialog}
        onAddItem={handleAddItem}
      />
    </>
  );
}
